import { useEffect, useState, type FormEvent } from "react";
import { postRsvp } from "../../api/client";
import { useEventContent } from "../../context/EventContentContext";
import { useRsvpReservation } from "../../hooks/useRsvpReservation";
import { useBotProtection } from "../../hooks/useBotProtection";
import { saveRsvpReservation } from "../../lib/rsvpAck";
import { HoneypotField } from "./HoneypotField";
import { TurnstileWidget } from "./TurnstileWidget";

type State = "idle" | "loading" | "err";

export function RsvpForm({
  eventId,
  onSuccess,
  thanksLine = "See you at the overnight.",
}: {
  eventId?: number | null;
  onSuccess?: () => void;
  /** Shown after a successful RSVP (e.g. "See you on Friday, 22 May 2026.") */
  thanksLine?: string;
}) {
  const content = useEventContent();
  const resolvedEventId = eventId ?? content.eventId ?? null;
  const reservation = useRsvpReservation(resolvedEventId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [seats, setSeats] = useState(1);
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string | null>(null);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  useEffect(() => {
    if (!reservation) return;
    setName(reservation.name);
    setEmail(reservation.email);
    setPhone(reservation.phone);
    setSeats(reservation.seats);
  }, [reservation]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!ready) return;
    setState("loading");
    setError(null);
    try {
      await postRsvp({ name, email, phone: phone || undefined, seats, ...botFields });
      saveRsvpReservation({
        eventId: resolvedEventId,
        name,
        email,
        phone,
        seats,
      });
      setEditing(false);
      setState("idle");
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save RSVP");
      setState("err");
      resetBotProtection();
    }
  }

  function bumpSeats(delta: number) {
    setSeats((n) => Math.max(1, Math.min(20, n + delta)));
  }

  if (reservation && !editing) {
    const who = reservation.name.trim();
    return (
      <div className="form-success form-success--card">
        <div className="sec-eyebrow">
          <em>seat reserved</em>
        </div>
        <h3 className="sec-title sec-title-sm">{thanksLine}</h3>
        <ul className="form-success-meta">
          {who ? <li>{who}</li> : null}
          <li>
            {reservation.seats} seat{reservation.seats === 1 ? "" : "s"}
          </li>
          {reservation.email ? <li>{reservation.email}</li> : null}
        </ul>
        <p className="form-hint">You can change this anytime before the night.</p>
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)}>
            Change reservation
          </button>
        </div>
      </div>
    );
  }

  const busy = state === "loading";

  return (
    <form className="form-stack" onSubmit={onSubmit} aria-busy={busy}>
      <HoneypotField value={website} onChange={setWebsite} />

      <div className="form-row">
        <label>
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={busy}
            autoComplete="name"
            placeholder="Your name"
          />
        </label>
        <label>
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </label>
      </div>

      <label>
        <span>Phone <em className="form-label-opt">optional</em></span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
          autoComplete="tel"
          placeholder="+256…"
        />
        <span className="form-hint">For seat reminders only — never shared.</span>
      </label>

      <div className="form-field">
        <span className="form-field-label">Seats</span>
        <div className="seat-stepper">
          <button
            type="button"
            className="seat-stepper-btn"
            aria-label="Fewer seats"
            disabled={busy || seats <= 1}
            onClick={() => bumpSeats(-1)}
          >
            −
          </button>
          <input
            type="number"
            min={1}
            max={20}
            value={seats}
            aria-label="Number of seats"
            onChange={(e) => setSeats(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            disabled={busy}
          />
          <button
            type="button"
            className="seat-stepper-btn"
            aria-label="More seats"
            disabled={busy || seats >= 20}
            onClick={() => bumpSeats(1)}
          >
            +
          </button>
        </div>
        <span className="form-hint">Up to 20 seats per reservation.</span>
      </div>

      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}

      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !ready}>
          {busy ? "Saving…" : reservation ? "Update reservation" : "Reserve seats"}
        </button>
        {reservation && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => {
              setEditing(false);
              setName(reservation.name);
              setEmail(reservation.email);
              setPhone(reservation.phone);
              setSeats(reservation.seats);
              setError(null);
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
